import { Lock as LockIcon, Search as SearchIcon } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { staticMenuItems } from '../config/menuConfig';
import { useAuth } from '../contexts/AuthContext';
import { useModules } from '../contexts/ModulesContext';
import { presentMenuItems } from '../domain/menuVisibility';
import { type ModuleUnavailableReason, moduleUnavailableMessageKey } from '../domain/modules';
import { menuLabelKey, pageKeyOf, resolveMenuLabel } from '../domain/pagePermissions';
import { isPageSearchShortcut, type PageSearchEntry, searchPages } from '../domain/pageSearch';
import { menuIconFor } from './menuIcons';

interface PageOption extends PageSearchEntry {
    /** Set when the page's module is unavailable: listed greyed out, as in the sidebar. */
    disabledReason: ModuleUnavailableReason | null;
}

/**
 * The header's page search: type part of a page's name and open it. Ctrl+K (Cmd+K) focuses it from
 * anywhere. It offers exactly the pages the sidebar shows the signed-in user.
 */
const HeaderPageSearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { modules } = useModules();
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const role = user?.role;
    const allowedPages = user?.allowedPages;

    const options = useMemo<PageOption[]>(
        () =>
            presentMenuItems(staticMenuItems, { role, allowedPages }, modules).map(
                ({ item, disabledReason }) => {
                    const translationKey = menuLabelKey(item.path);
                    return {
                        key: pageKeyOf(item.path),
                        path: item.path,
                        label: resolveMenuLabel(t(translationKey), translationKey, item.name),
                        name: item.name,
                        disabledReason,
                    };
                },
            ),
        [role, allowedPages, modules, t],
    );

    useEffect(() => {
        const focusOnShortcut = (event: KeyboardEvent) => {
            if (isPageSearchShortcut(event)) {
                // The browser's own Ctrl+K would move the focus to its address bar.
                event.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', focusOnShortcut);
        return () => window.removeEventListener('keydown', focusOnShortcut);
    }, []);

    const leave = () => {
        setInputValue('');
        inputRef.current?.blur();
    };

    return (
        <Autocomplete<PageOption>
            data-testid="header-page-search"
            size="small"
            openOnFocus
            autoHighlight
            options={options}
            value={null}
            inputValue={inputValue}
            onInputChange={(_event, value, reason) => {
                // Choosing an option would otherwise write its label into the field.
                if (reason !== 'reset') {
                    setInputValue(value);
                }
            }}
            onChange={(_event, option) => {
                if (option !== null) {
                    leave();
                    navigate(option.path);
                }
            }}
            filterOptions={(candidates, state) => searchPages(candidates, state.inputValue)}
            getOptionLabel={option => option.label}
            getOptionDisabled={option => option.disabledReason !== null}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            noOptionsText={t('header.pageSearch.noResults')}
            sx={{ width: { xs: 150, md: 240 }, ml: 1 }}
            renderOption={(props, option) => {
                // MUI hands the key inside the props; React wants it passed on its own.
                const { key, ...optionProps } = props;
                return (
                    <Box
                        key={key}
                        component="li"
                        {...optionProps}
                        data-testid={`header-page-search-option-${option.key}`}
                    >
                        <ListItemIcon sx={{ minWidth: 32 }}>{menuIconFor(option.key)}</ListItemIcon>
                        <ListItemText
                            primary={option.label}
                            secondary={
                                option.disabledReason === null
                                    ? null
                                    : t(moduleUnavailableMessageKey(option.disabledReason))
                            }
                            slotProps={{ primary: { fontSize: '0.8125rem' } }}
                        />
                        {option.disabledReason !== null && (
                            <LockIcon sx={{ fontSize: 14, ml: 1 }} aria-hidden />
                        )}
                    </Box>
                );
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    inputRef={inputRef}
                    placeholder={t('header.pageSearch.placeholder')}
                    onKeyDown={event => {
                        if (event.key === 'Escape') {
                            leave();
                        }
                    }}
                    slotProps={{
                        htmlInput: {
                            ...params.inputProps,
                            'aria-label': t('header.pageSearch.placeholder'),
                        },
                        input: {
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: { xs: 'none', md: 'block' }, mr: 0.5 }}
                                        aria-hidden
                                    >
                                        {t('header.pageSearch.shortcutHint')}
                                    </Typography>
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
        />
    );
};

export default HeaderPageSearch;
